import api from "./api"
import { showErroPage, sortByAttribute } from "../utils/helper"
import { AxiosError } from "axios"
import { UpdateCompanyEmailRequest } from "../Interfaces"

// New interfaces for structured API responses
interface ValidationErrors {
  [field: string]: string | number | string[];
}

interface ApiResponse {
  success: boolean;
  data?: any;
  validationErrors?: ValidationErrors;
  error?: string;
  message?: string;
}

// Helper function to format validation errors
const formatValidationErrors = (validationErrors: ValidationErrors): string => {
  if (!validationErrors) return 'Validation failed';
  return Object.entries(validationErrors)
    .filter(([key]) => typeof validationErrors[key] !== 'number' || key === 'existingUserId')
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        return `${field}: ${value.join(', ')}`;
      }
      return `${field}: ${value}`;
    })
    .join('; ');
};

interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  role: string
}

class UserService {

  private baseUrl = "/user";


  public createAdmin = async (userInfo: any, imageFile: File | null = null) => {
    if (!userInfo) throw new Error("No user info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}/admin`,
        { userInfo }
      );

      const apiResponse: ApiResponse = response.data;

      // Handle structured validation errors
      if (!apiResponse.success) {
        if (apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
          const errorMessage = formatValidationErrors(apiResponse.validationErrors);
          throw new Error(errorMessage);
        } else {
          throw new Error(apiResponse.error || apiResponse.message || 'Admin creation failed');
        }
      }

      if (imageFile) await this.uploadProfileImage(imageFile, apiResponse.data.id, apiResponse.data.firstName, apiResponse.data.lastName)
      return apiResponse.data || response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Check if it's a structured error response
        if (error.response?.data && typeof error.response.data === 'object') {
          const apiResponse: ApiResponse = error.response.data;
          if (!apiResponse.success && apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
            const errorMessage = formatValidationErrors(apiResponse.validationErrors);
            throw new Error(errorMessage);
          }
        }
        throw Error(error.response?.statusText || 'Admin creation failed')
      } else {
        console.error(error);
        throw error;
      }
    }
  }
  public createOwner = async (userInfo: any, imageFile: File | null = null) => {
    if (!userInfo) throw new Error("No user info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}/owner`,
        { userInfo },
      );

      const apiResponse: ApiResponse = response.data;

      // Handle structured validation errors
      if (!apiResponse.success) {
        if (apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
          const errorMessage = formatValidationErrors(apiResponse.validationErrors);
          throw new Error(errorMessage);
        } else {
          throw new Error(apiResponse.error || apiResponse.message || 'Owner creation failed');
        }
      }

      if (imageFile) await this.uploadProfileImage(imageFile, apiResponse.data.id, apiResponse.data.firstName, apiResponse.data.lastName)
      return apiResponse.data || response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Check if it's a structured error response
        if (error.response?.data && typeof error.response.data === 'object') {
          const apiResponse: ApiResponse = error.response.data;
          if (!apiResponse.success && apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
            const errorMessage = formatValidationErrors(apiResponse.validationErrors);
            throw new Error(errorMessage);
          }
        }
        throw Error(error.response?.statusText || 'Owner creation failed')
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public createCompanyManager = async (userInfo: any, imageFile: File | null = null) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/company-manager`,
        { userInfo },
      );

      const apiResponse: ApiResponse = response.data;

      // Handle structured validation errors
      if (!apiResponse.success) {
        if (apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
          const errorMessage = formatValidationErrors(apiResponse.validationErrors);
          throw new Error(errorMessage);
        } else {
          throw new Error(apiResponse.error || apiResponse.message || 'Company Manager creation failed');
        }
      }

      if (imageFile) await this.uploadProfileImage(imageFile, apiResponse.data.id, apiResponse.data.firstName, apiResponse.data.lastName)
      return apiResponse.data || response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Check if it's a structured error response
        if (error.response?.data && typeof error.response.data === 'object') {
          const apiResponse: ApiResponse = error.response.data;
          if (!apiResponse.success && apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
            const errorMessage = formatValidationErrors(apiResponse.validationErrors);
            throw new Error(errorMessage);
          }
        }
        throw Error(error.response?.statusText || 'Company Manager creation failed')
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public createAreaManager = async (userInfo: any, organizationIds: number[], imageFile: File | null = null) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/area-manager`,
        { userInfo, organizationIds },
      );

      const apiResponse: ApiResponse = response.data;

      // Handle structured validation errors
      if (!apiResponse.success) {
        if (apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
          const errorMessage = formatValidationErrors(apiResponse.validationErrors);
          throw new Error(errorMessage);
        } else {
          throw new Error(apiResponse.error || apiResponse.message || 'Area Manager creation failed');
        }
      }

      if (imageFile) await this.uploadProfileImage(imageFile, apiResponse.data.id, apiResponse.data.firstName, apiResponse.data.lastName)
      return apiResponse.data || response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        // Check if it's a structured error response
        if (error.response?.data && typeof error.response.data === 'object') {
          const apiResponse: ApiResponse = error.response.data;
          if (!apiResponse.success && apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
            const errorMessage = formatValidationErrors(apiResponse.validationErrors);
            throw new Error(errorMessage);
          }
        }
        throw Error(error.response?.statusText || 'Area Manager creation failed')
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public createManager = async (userInfo: any, locationIds: number[], imageFile: File | null = null) => {
    console.log("createManager called with userInfo:", userInfo)
    console.log("createManager called with locationIds:", locationIds)

    try {
      const response = await api.post(
        `${this.baseUrl}/manager`,
        { userInfo, locationIds },
      );
      console.log("createManager response:", response.data)

      const apiResponse: ApiResponse = response.data;

      // Handle structured validation errors
      if (!apiResponse.success) {
        if (apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
          const errorMessage = formatValidationErrors(apiResponse.validationErrors);
          throw new Error(errorMessage);
        } else {
          throw new Error(apiResponse.error || apiResponse.message || 'Manager creation failed');
        }
      }

      // wait for image to upload before returning
      if (imageFile) await this.uploadProfileImage(imageFile, apiResponse.data.id, apiResponse.data.firstName, apiResponse.data.lastName)
      return apiResponse.data || response.data
    } catch (error) {
      console.error("createManager error:", error)
      if (error instanceof AxiosError) {
        console.error("createManager Axios error response:", error.response?.data)
        console.error("createManager Axios error status:", error.response?.status)
        // Check if it's a structured error response
        if (error.response?.data && typeof error.response.data === 'object') {
          const apiResponse: ApiResponse = error.response.data;
          if (!apiResponse.success && apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
            const errorMessage = formatValidationErrors(apiResponse.validationErrors);
            throw new Error(errorMessage);
          }
        }
        throw Error(error.response?.statusText || 'Manager creation failed')
      } else {
        console.error("createManager non-Axios error:", error);
        throw error;
      }
    }
  }

  public createAssistantManager = async (userInfo: any, locationIds: number[], imageFile: File | null = null) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/assistant-manager`,
        { userInfo, locationIds },
      );

      const apiResponse: ApiResponse = response.data;

      // Handle structured validation errors
      if (!apiResponse.success) {
        if (apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
          const errorMessage = formatValidationErrors(apiResponse.validationErrors);
          throw new Error(errorMessage);
        } else {
          throw new Error(apiResponse.error || apiResponse.message || 'Assistant Manager creation failed');
        }
      }

      // wait for image to upload before returning
      if (imageFile) await this.uploadProfileImage(imageFile, apiResponse.data.id, apiResponse.data.firstName, apiResponse.data.lastName)
      return apiResponse.data || response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Check if it's a structured error response
        if (error.response?.data && typeof error.response.data === 'object') {
          const apiResponse: ApiResponse = error.response.data;
          if (!apiResponse.success && apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
            const errorMessage = formatValidationErrors(apiResponse.validationErrors);
            throw new Error(errorMessage);
          }
        }
        throw Error(error.response?.statusText || 'Assistant Manager creation failed')
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public uploadProfileImage = async (imageFile: File, userId: number, firstName: string, lastName: string) => {
    if (!imageFile) return;
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/update/profile-image/${userId}/${firstName}-${lastName}`,
      )
      if (imageFile.type !== "image/jpeg" && imageFile.type !== "image/png" && imageFile.type !== "image/jpg") throw new Error("Invalid file type")
      const response = await api.put(
        signedUrl,
        imageFile,
        {
          headers: {
            "Content-Type": imageFile.type,
            "Authorization": undefined,
          }
        }
      );
      return response.data;
    } catch (error) {
      return error as Error;
    }
  };

  public getViewableProfileImage = async (userId: number, firstName: string, lastName: string) => {
    try {
      const { data: signedUrl } = await api.get(
        `${this.baseUrl}/profile-image/${userId}/${firstName}-${lastName}`, {
        timeout: 5000
      }
      )
      return signedUrl;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public getOne = async (userId: number) => {
    if (!userId || userId === 0) {
      throw new Error("Invalid user ID")
    }

    try {
      const response = await api.get(
        `${this.baseUrl}/${userId}`
      );

      if (!response.data) {
        throw new Error("No user data received")
      }

      return response.data;
    } catch (error) {
      console.error("Error in getOne for userId:", userId, error)
      if (error instanceof AxiosError) {
        console.error("Axios error in getOne:", error.response);
        throw new Error(error.response?.statusText || "Failed to fetch user data")
      } else {
        console.error("Non-axios error in getOne:", error);
        throw error instanceof Error ? error : new Error("An unknown error occurred")
      }
    }
  }

  public getAllByUser = async (userId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/all/by-user/${userId}`
      )
      return { users: response.data }
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error("Error in getAllByUser:", error.response?.data || error.message);
        throw new Error(error.response?.data?.message || error.message || "Failed to fetch users")
      } else {
        console.error("Non-axios error in getAllByUser:", error);
        throw new Error("An unexpected error occurred while fetching users")
      }
    }
  }
  public getSomeByUser = async (userId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-user/${userId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      )
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }

  public getAllByLocation = async (locationId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/all/by-location/${locationId}`
      )
      sortByAttribute(response.data, "lastName")
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }
  public getSomeByLocation = async (locationId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-location/${locationId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      )
      // sortByAttribute(response.data, "lastName")
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }

  public getAllByOrganization = async (organizationId: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/all/by-organization/${organizationId}`
      )
      sortByAttribute(response.data, "lastName")
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }
  public getSomeByOrganization = async (organizationId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-organization/${organizationId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      )
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        // Do something with this error...
        throw new Error("axios error")
      } else {
        console.error(error)
        showErroPage()
      }
    }
  }

  public updateTableOrder = async (tableId: number, tableName: string, displayOrder: number, id?: number) => {
    try {
      if (!id) {
        const response = await api.post(`${this.baseUrl}/create/table-order`, {
          tableName: tableName,
          tableId: tableId,
          displayOrder: displayOrder
        });
        return response.data
      }
      const response = await api.put(`${this.baseUrl}/update/table-order/${id}`, {
        tableName: tableName,
        tableId: tableId,
        displayOrder: displayOrder
      })
      return response.data
    } catch (error) {
      console.error("Failed to update order preferences:", error);
    }
  };

  public update = async (userInfo: any, organizationIds: number[] = [], locationIds: number[] = [], imageFile: File | null,) => {
    try {
      const response = await api.put(
        `${this.baseUrl}/update/user/${userInfo.id}`,
        { userInfo, organizationIds, locationIds },
      )

      const apiResponse: ApiResponse = response.data;

      // Handle structured validation errors
      if (!apiResponse.success) {
        if (apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
          const errorMessage = formatValidationErrors(apiResponse.validationErrors);
          throw new Error(errorMessage);
        } else {
          throw new Error(apiResponse.error || apiResponse.message || 'Update failed');
        }
      }

      if (imageFile) await this.uploadProfileImage(imageFile, response.data.data.id, response.data.data.firstName, response.data.data.lastName)
      return apiResponse.data || response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response)
        // Check if it's a structured error response
        if (error.response?.data && typeof error.response.data === 'object') {
          const apiResponse: ApiResponse = error.response.data;
          if (!apiResponse.success && apiResponse.validationErrors && Object.keys(apiResponse.validationErrors).length > 0) {
            const errorMessage = formatValidationErrors(apiResponse.validationErrors);
            throw new Error(errorMessage);
          }
        }
        throw Error(error.response?.statusText || 'Update failed')
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public updatePassword = async (userId: number, newPassword: string) => {
    try {
      const response = await api.put(
        `${this.baseUrl}/update/user/${userId}`,
        {
          userInfo: {
            id: userId,
            password: newPassword
          },
          organizationIds: [],
          locationIds: []
        }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public updateCompanyEmail = async (userId: number, companyEmail: boolean) => {
    try {
      const requestData: UpdateCompanyEmailRequest = {
        userId: userId,
        company_email: companyEmail
      };

      const response = await api.put(
        `${this.baseUrl}/update/company-email`,
        requestData
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
        throw error;
      }
    }
  }

  public delete = async (id: number) => {
    try {
      const response = await api.delete(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public addUsersToLocation = async (userIds: number[], locationId: number) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/add-to-location`,
        { userIds, locationId }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  public removeUserFromLocation = async (userId: number, locationId: number) => {
    try {
      const response = await api.delete(
        `${this.baseUrl}/remove-from-location/${userId}/${locationId}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  public addUsersToOrganization = async (userIds: number[], organizationId: number) => {
    try {
      const response = await api.post(
        `${this.baseUrl}/add-to-organization`,
        { userIds, organizationId }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  public removeUserFromOrganization = async (userId: number, organizationId: number) => {
    try {
      const response = await api.delete(
        `${this.baseUrl}/remove-from-organization/${userId}/${organizationId}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText);
      } else {
        console.error(error);
      }
    }
  }

  async login(email: string, password: string): Promise<boolean> {
    try {
      const response = await api.post('/auth/login', { email, password })
      if (response.data.token) {
        localStorage.setItem('token', response.data.token)
        return true
      }
      return false
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  async logout(): Promise<void> {
    localStorage.removeItem('token')
  }

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/auth/me')
    return response.data
  }

  async checkPasswordResetRequired(userId: number): Promise<boolean> {
    try {
      const response = await api.get(`/users/${userId}/password-reset-required`)
      return response.data.requiresReset
    } catch (error) {
      console.error('Failed to check password reset status:', error)
      return false
    }
  }
}

export const userService = new UserService()